// Minimal anonymous authentication for Chipless
import { getAuth, signInAnonymously, connectAuthEmulator } from 'firebase/auth';
import app from '../firebase/firebaseConfig';

export const auth = getAuth(app);

if (process.env.REACT_APP_FIREBASE_EMULATOR === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
}

// Shared in-flight sign-in. Firebase's signInAnonymously reuses an existing
// anonymous user (it awaits its own rehydration first, so returning visitors
// are fine), but it does NOT dedup calls that are still in flight: on a
// first-ever visit two simultaneous callers both see currentUser == null and
// each create a *separate* anonymous account, last-write-wins on currentUser.
// The caller handed the losing uid keeps using it — so the room can be written
// under one uid while PostHog identifies another, and a later reload can fail
// the `player.id === uid` restore check in Home.jsx and silently drop the
// player's seat. Realistically triggered by tapping Create/Join on a first-ever
// visit before the initial auth round-trip lands (e.g. a QR deep link).
let signInPromise = null;

/**
 * Ensure user is authenticated anonymously
 * @returns {Promise<string>} User ID
 */
export async function ensureAuth() {
  if (auth.currentUser) {
    return auth.currentUser.uid;
  }

  if (!signInPromise) {
    signInPromise = signInAnonymously(auth)
      .then((result) => {
        // Resolved only after Firebase has set currentUser, so later callers
        // take the fast path above and never see this cleared promise.
        signInPromise = null;
        console.log('🔐 Anonymous auth successful:', result.user.uid);
        return result.user.uid;
      })
      .catch((error) => {
        signInPromise = null; // let the next caller retry
        console.error('🔐 Anonymous auth failed:', error);
        throw new Error('Authentication failed. Please refresh the page.');
      });
  }

  return signInPromise;
}
